import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Tpl3plService, FailedExportMissingSkuReportRow } from '../../../services/tpl3pl.service';

export interface FailedExportMissingSkusDialogData {
	tpl3plService: Tpl3plService;
	/** Match export queue “include archived” when opening from the queue page. */
	includeArchived?: boolean;
	days?: number;
	limit?: number;
}

@Component({
	selector: 'app-failed-export-missing-skus-dialog',
	template: `
		<h2 mat-dialog-title>Missing SKUs (failed exports)</h2>
		<mat-dialog-content class="fems-body">
			<p class="text-muted small mb-2" *ngIf="!loading && !error && report">
				Generated {{ report.generated_at | date:'medium' }} · Scanned {{ report.queue_rows_scanned }} queue row(s) ·
				{{ report.unique_sku_count }} unique SKU(s)
			</p>
			<mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>
			<p *ngIf="error" class="text-danger">{{ error }}</p>
			<ng-container *ngIf="report && !loading">
				<div class="fems-csv-header">
					<div class="d-flex justify-content-between align-items-center mb-1">
						<label class="mb-0 font-weight-bold" for="femsCsv">CSV (SKU only, one per line)</label>
						<button mat-stroked-button type="button" (click)="copyCsv()" [disabled]="!report.csv">
							<mat-icon>content_copy</mat-icon> Copy CSV
						</button>
					</div>
					<textarea id="femsCsv" class="fems-csv-textarea" readonly rows="5">{{ report.csv }}</textarea>
				</div>
				<div class="fems-details mt-3">
					<h3 class="h6 mb-2">ERP details</h3>
					<div *ngFor="let row of report.skus" class="fems-sku-block mb-3">
						<div class="fems-sku-title font-weight-bold">{{ row.sku }}</div>
						<div *ngIf="row.stockmaster as sm" class="small text-muted mb-1">
							{{ sm.description }}
							<span *ngIf="sm.longdescription" class="d-block mt-1 fems-long-desc">{{ sm.longdescription }}</span>
						</div>
						<div *ngIf="!row.stockmaster" class="small text-warning mb-1">No stockmaster row for this SKU id.</div>
						<div *ngIf="row.ordernos?.length" class="small mb-1">
							<strong>Queue order #:</strong> {{ row.ordernos.join(', ') }}
						</div>
						<table class="table table-sm table-bordered mb-0 fems-loc-table" *ngIf="row.locstock?.length">
							<thead>
								<tr>
									<th>Loc</th>
									<th>Bin</th>
									<th>Qty</th>
									<th>Committed</th>
									<th>Reorder</th>
								</tr>
							</thead>
							<tbody>
								<tr *ngFor="let loc of row.locstock">
									<td>{{ loc.loccode }}</td>
									<td>{{ loc.bin }}</td>
									<td>{{ loc.quantity }}</td>
									<td>{{ loc.commited }}</td>
									<td>{{ loc.reorderlevel }}</td>
								</tr>
							</tbody>
						</table>
						<p *ngIf="!row.locstock?.length" class="small text-muted mb-0">No locstock rows.</p>
					</div>
				</div>
			</ng-container>
		</mat-dialog-content>
		<mat-dialog-actions align="end">
			<button mat-button type="button" mat-dialog-close>Close</button>
		</mat-dialog-actions>
	`,
	styles: [
		`
			.fems-body {
				min-width: 280px;
				max-width: 860px;
				max-height: 70vh;
				overflow: auto;
			}
			.fems-csv-textarea {
				width: 100%;
				font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
				font-size: 11px;
				box-sizing: border-box;
			}
			.fems-long-desc {
				white-space: pre-wrap;
				max-height: 4.5rem;
				overflow: hidden;
			}
			.fems-sku-title {
				font-size: 1rem;
			}
			.fems-loc-table th,
			.fems-loc-table td {
				font-size: 0.82rem;
			}
		`
	]
})
export class FailedExportMissingSkusDialogComponent implements OnInit {
	loading = true;
	error: string | null = null;
	report: {
		generated_at: string;
		queue_rows_scanned: number;
		unique_sku_count: number;
		csv: string;
		skus: FailedExportMissingSkuReportRow[];
	} | null = null;

	constructor(@Inject(MAT_DIALOG_DATA) public data: FailedExportMissingSkusDialogData) {}

	ngOnInit(): void {
		const days = this.data.days != null && this.data.days > 0 ? this.data.days : 90;
		const limit = this.data.limit != null && this.data.limit > 0 ? this.data.limit : 2000;
		const includeArchived = !!this.data.includeArchived;
		this.data.tpl3plService.getFailedExportMissingSkus({ days, limit, include_archived: includeArchived }).subscribe({
			next: (res) => {
				this.loading = false;
				if (!res?.success) {
					this.error = res?.message || 'Report failed';
					return;
				}
				this.report = {
					generated_at: res.generated_at || new Date().toISOString(),
					queue_rows_scanned: res.queue_rows_scanned ?? 0,
					unique_sku_count: res.unique_sku_count ?? 0,
					csv: res.csv || '',
					skus: Array.isArray(res.skus) ? res.skus : []
				};
			},
			error: (err) => {
				this.loading = false;
				this.error = err?.error?.message || err?.message || 'Request failed';
			}
		});
	}

	copyCsv(): void {
		const t = this.report?.csv;
		if (!t) {
			return;
		}
		if (navigator.clipboard && navigator.clipboard.writeText) {
			navigator.clipboard.writeText(t).catch(() => {
				this.fallbackCopy(t);
			});
		} else {
			this.fallbackCopy(t);
		}
	}

	private fallbackCopy(text: string): void {
		const ta = document.createElement('textarea');
		ta.value = text;
		document.body.appendChild(ta);
		ta.select();
		try {
			document.execCommand('copy');
		} catch {
			/* ignore */
		}
		document.body.removeChild(ta);
	}
}
